import { userManager } from './oidc';
import axios, { AxiosResponse } from 'axios'; // TODO replace with fetch API

export interface RestError {
  code: string;
  message?: string;
  invalidField?: string; // only if code is "INVALID_PARAMETER"
}

export interface SetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
  isTCAccepted?: boolean;
  invalidateSessions?: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  invalidateSessions: boolean;
}

export interface ResetPasswordRequest {
  email: string;
  locale?: string;
}

export interface ResendOTPRequest {
  email: string;
  locale?: string;
}

export interface ValidateOTPRequest {
  email: string;
  code: string;
}

export interface ConfirmUserRequest {
  email: string;
  token: string;
}

export interface ConfirmUserResponse {
  token: string;
  showTC?: boolean;
}

export interface ChangeRequest {
  op: 'add' | 'update' | 'remove';
  path: string;
  value?: any;
}

export type OrganizationSummaryDTO = {
  _id: string;
  name: string;
  crmAccountNumber: string;
}

export type OrganizationDTO = {
  _id: string;
  name: string;
  crmAccountNumber: string;

  parent: string;

  licenses: string[];
  countries: string[];
  userAdministrators: string[];
  primaryAdmin: string;

  businessType: string;
  businessTypeCode: number;
}

export type SalutationCode = 'male' | 'female' | 'other';

export type FederatedAccount = {
  type: 'EMPLOYEE' |'USA',
  id: string,
}

export type UserDTO = {
  _id: string;
  email: string;
  salutation?: SalutationCode;
  firstName: string;
  lastName: string;
  preferredLanguage: string;
  latestAgbAccepted: boolean;
  lastOathUse?: string;
  organization: string;
  organizationRefObject?: OrganizationSummaryDTO;
  roles: string[];
  federatedAccounts?: FederatedAccount[];
}

const toJson = async (resp: Response) => {
  if (!resp?.ok) throw resp;
  return resp.json();
};

export default class Backend {

  static url = import.meta.env.VITE_DSS_SVC + '/v2';
  static baseConfig: Record<string, any> = {
    headers: {
      'Accept': 'application/json',
    }
  };

  static {
    // interceptor for handling 401
    axios.interceptors.response.use(function (response) {
      return response;
    }, function (error) {
      if (401 === error.response.status) {
        console.info('[axios:interceptor] start login');
        userManager.startLogin('/');
      } else {
        return Promise.reject(error);
      }
    });
  }

  config: Record<string, any>;

  constructor(accessToken: string) {
    this.config = {
      ...Backend.baseConfig,
      headers: {
        'Authorization': 'Bearer ' + accessToken,
        'Content-Type': 'application/json',
      }
    };
  }

  /**
   * builds a Backend instance if there is a valid access_token available
   */
  static async validateToken(): Promise<Backend> {
    const u = await userManager.getValidUser();
    return new Backend(u.access_token);
  }

  /// PASSWORD ///////////////////////////////////////////////////////////////////////////////////////////////////////

  static setPassword = async (req: SetPasswordRequest) => axios.post(`${this.url}/password`, req, this.baseConfig);

  changePassword = async (req: ChangePasswordRequest) => axios.patch(`${Backend.url}/password`, req, this.config);

  static resetPassword = async (req: ResetPasswordRequest) => axios.put(`${this.url}/password`, req, this.baseConfig);

  static resendOTP = async (req: ResendOTPRequest) => axios.put(`${this.url}/password`, req, this.baseConfig);

  static validateOTP = async (req: ValidateOTPRequest) =>
    axios.post<ConfirmUserResponse>(`${this.url}/otp`, req, this.baseConfig);

  /// OATH TOTP ///////////////////////////////////////////////////////////////////////////////////////////////////////

  deleteOathDevice = async () => (
    axios.delete(`${Backend.url}/device`, this.config)
  );

  /// PASSKEYS ////////////////////////////////////////////////////////////////////////////////////////////////////////

  loadPasskeys = async (): Promise<Record<string, string>> => fetch(`${Backend.url}/passkeys`, this.config)
    .then(toJson);

  renamePasskey = async (uuid: string, name: string) => (
    fetch(`${Backend.url}/passkeys/${uuid}`, {
      ...this.config,
      method: 'PATCH',
      body: JSON.stringify({
        name
      })
    })
  );

  deletePasskey = async (uuid: string) => (
    fetch(`${Backend.url}/passkeys/${uuid}`, {
      method: 'DELETE',
      ...this.config
    })
  );

  /// USER ////////////////////////////////////////////////////////////////////////////////////////////////////////////

  loadUserProfile = async (): Promise<AxiosResponse<UserDTO>> => axios.get(`${Backend.url}/`, this.config);

  static confirmUser = async (req: ConfirmUserRequest) =>
    axios.post<ConfirmUserResponse>(`${this.url}/`, req, this.baseConfig);

  updateUser = async (req: ChangeRequest[]) => axios.patch(`${Backend.url}/`, req, this.config);

  getOrganization = async (): Promise<AxiosResponse<OrganizationDTO>> => (axios.get(`${Backend.url}/organization`, this.config));
}
